import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import {
  Plane,
  Clock,
  Truck,
  Ship,
  Package,
  Shield,
  ArrowRight,
  CheckCircle,
  Phone,
  MessageCircle,
  PhoneCall,
  Route,
  Boxes,
  PackageCheck,
  Building2,
  Car,
  Pill,
  Factory,
  Shield as ShieldIcon,
  Cpu,
  Zap,
  Gem,
} from 'lucide-react';
import Marquee from '../components/Marquee';
import SEO from '../components/SEO';
import { fetchLatest, mediaUrl } from '../lib/blogApi';

const Home = () => {
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 500], [0, 120]);
  const [latest, setLatest] = React.useState([]);

  React.useEffect(() => {
    fetchLatest().then(setLatest).catch(() => setLatest([]));
  }, []);

  const fmtDate = (d) => { try { return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return ''; } };

  const services = [
    {
      number: '01',
      icon: Clock,
      title: 'Time-Critical & AOG',
      description: 'Immediate response for aircraft-on-ground situations with next-flight-out solutions and dedicated project management.',
      link: '/time-critical',
    },
    {
      number: '02',
      icon: Plane,
      title: 'Air Freight',
      description: 'Global air freight solutions with priority routing, customs expertise and real-time tracking for urgent shipments.',
      link: '/air-freight',
    },
    {
      number: '03',
      icon: Truck,
      title: 'Road Freight',
      description: 'Dedicated express vehicles across the UK and Europe with secure, temperature-controlled and time-definite delivery.',
      link: '/road-freight',
    },
    {
      number: '04',
      icon: Ship,
      title: 'Sea Freight',
      description: 'Cost-effective ocean freight for less urgent cargo with full container, groupage and port-to-door services.',
      link: '/sea-freight',
    },
    {
      number: '05',
      icon: Package,
      title: 'Onboard Courier',
      description: 'Hand-carry services via commercial flights or private aircraft charter for ultra-critical aerospace components.',
      link: '/onboard-courier',
    },
    {
      number: '06',
      icon: Shield,
      title: 'Dangerous Goods',
      description: 'Compliant coordination of hazardous materials with trained specialists and approved carrier partners across IATA, ADR and IMDG.',
      link: '/dangerous-goods',
    },
  ];

  const stats = [
    { number: '24/7', label: 'Available Support' },
    { number: '150+', label: 'Countries Served' },
    { number: '<2hrs', label: 'AOG Response Time' },
    { number: '99.8%', label: 'On-Time Delivery' },
  ];

  const features = [
    'Proactive milestone updates',
    'Dedicated account management',
    'Dangerous goods coordination',
    'Temperature-controlled options',
    'Customs coordination',
    'Insurance & security',
  ];

  const howItWorks = [
    { icon: PhoneCall, step: '01', title: 'Contact ATC', desc: 'Reach our team 24/7 by phone, email or WhatsApp with your requirement.' },
    { icon: Route, step: '02', title: 'Route Assessment', desc: 'Receive an immediate route assessment and the fastest viable solution.' },
    { icon: Boxes, step: '03', title: 'Collection & Milestones', desc: 'We arrange collection and manage every milestone with proactive updates.' },
    { icon: PackageCheck, step: '04', title: 'Delivery & POD', desc: 'Final delivery completed with confirmed proof of delivery.' },
  ];

  const industries = [
    { icon: Plane, name: 'Aviation & Aerospace' },
    { icon: Car, name: 'Automotive' },
    { icon: Pill, name: 'Pharmaceutical & Healthcare' },
    { icon: Factory, name: 'Manufacturing' },
    { icon: ShieldIcon, name: 'Defence' },
    { icon: Cpu, name: 'Technology' },
    { icon: Zap, name: 'Energy' },
    { icon: Gem, name: 'High-Value Goods' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Aero Time Critical | 24/7 AOG & Global Freight Solutions"
        description="Specialist time-critical, AOG and aerospace logistics. Global air, road and sea freight with 24/7 operational support and complete shipment control."
        path="/"
      />
      {/* Hero Section */}
      <section className="relative h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-[#002240]/95 via-[#003057]/85 to-[#003057]/60 z-10" />
          <motion.img
            style={{ y: heroY }}
            initial={{ scale: 1.15 }}
            animate={{ scale: 1 }}
            transition={{ duration: 8, ease: 'easeOut' }}
            src="https://customer-assets-m6fa6gv7.emergentagent.net/job_cargo-urgent/artifacts/esv8b4gm_AOG%20ATC%20.png"
            alt="Aero Time Critical branded van delivering an urgent aircraft part beside an aircraft under maintenance at dusk"
            className="w-full h-full object-cover"
            fetchPriority="high"
          />
        </div>

        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-28 md:pt-24">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 bg-[#C9A227] text-[#002240] px-4 py-2 rounded-full text-sm font-semibold mb-6"
            >
              <Clock size={16} />
              24/7/365 GLOBAL FREIGHT SPECIALISTS
            </motion.div>

            <motion.h1
              className="hero-title mb-6"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Global Freight.
              <br />
              <span className="text-[#00A3E0]">Critical Timing.</span>
              <br />
              Complete Control.
            </motion.h1>

            <motion.p
              className="hero-subtitle max-w-2xl mb-4"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              From routine worldwide transportation to urgent AOG movements, Aero Time Critical delivers
              reliable air, road and sea freight solutions backed by specialist routing expertise and 24/7
              operational support.
            </motion.p>

            <motion.p
              className="text-[#C9A227] font-semibold max-w-2xl mb-10 text-base md:text-lg"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.65 }}
            >
              Specialist time-critical logistics for aviation, aerospace and industries where operational
              delays are not an option.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row flex-wrap items-start gap-4"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <Link to="/quote">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-primary flex items-center gap-2"
                  data-testid="hero-get-quote-btn"
                >
                  Get a Quote
                  <ArrowRight size={20} />
                </motion.button>
              </Link>
              <a href="tel:+441753720055">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-secondary flex items-center gap-2"
                  data-testid="hero-urgent-btn"
                >
                  <Phone size={20} />
                  24/7 Urgent Shipment
                </motion.button>
              </a>
              <a href="https://wa.me/447445137566" target="_blank" rel="noopener noreferrer">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex items-center gap-2 bg-[#25D366] text-white px-6 py-[0.95rem] rounded font-semibold border-2 border-[#25D366]"
                  data-testid="hero-whatsapp-btn"
                >
                  <MessageCircle size={20} />
                  WhatsApp Urgent Support
                </motion.button>
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-white py-16 border-b border-[#E5E9EF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-5xl md:text-6xl font-bold text-[#0089D0] mb-2">
                  {stat.number}
                </div>
                <div className="text-[#4A4A4A] text-sm md:text-base">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Marquee */}
      <Marquee />

      {/* Services Section */}
      <section className="bg-[#F7F9FB] py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#0089D0] font-semibold tracking-wide uppercase text-sm">What We Do</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-3 mb-6 text-[#003057]">
              Our Services
            </h2>
            <p className="text-lg text-[#4A4A4A] max-w-3xl mx-auto">
              Comprehensive freight solutions tailored to your time-critical and specialist logistics requirements
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link to={service.link} data-testid={`service-card-${index + 1}`}>
                    <div className="service-card h-full">
                      <div className="relative">
                        <div className="section-number absolute -top-6 right-0">
                          {service.number}
                        </div>
                        <div className="w-14 h-14 bg-[#0089D0]/10 rounded-lg flex items-center justify-center mb-6">
                          <Icon className="text-[#0089D0]" size={28} />
                        </div>
                        <h3 className="text-2xl font-bold mb-4 text-[#003057]">{service.title}</h3>
                        <p className="text-[#4A4A4A] leading-relaxed mb-6">
                          {service.description}
                        </p>
                        <div className="flex items-center text-[#0089D0] font-semibold">
                          Learn More
                          <ArrowRight className="ml-2" size={18} />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#0089D0] font-semibold tracking-wide uppercase text-sm">Simple Process</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-3 mb-6 text-[#003057]">How It Works</h2>
            <p className="text-lg text-[#4A4A4A] max-w-3xl mx-auto">
              From first contact to proof of delivery, we manage every step of your time-critical shipment
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="relative bg-[#F7F9FB] p-8 rounded-lg border border-[#E5E9EF]"
                >
                  <div className="absolute top-6 right-6 text-4xl font-bold text-[#0089D0]/15">{item.step}</div>
                  <div className="w-14 h-14 bg-[#0089D0] rounded-lg flex items-center justify-center mb-5">
                    <Icon className="text-white" size={26} />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-[#003057]">{item.title}</h3>
                  <p className="text-[#4A4A4A] leading-relaxed">{item.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industries We Support */}
      <section className="bg-[#003057] py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#C9A227] font-semibold tracking-wide uppercase text-sm">Sectors We Serve</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-3 mb-6 text-white">Industries We Support</h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Trusted by demanding sectors where delays carry significant operational and financial impact
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {industries.map((industry, index) => {
              const Icon = industry.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/5 border border-white/10 rounded-lg p-6 text-center hover:bg-white/10 hover:border-[#C9A227] transition-all"
                >
                  <div className="w-12 h-12 bg-[#0089D0]/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <Icon className="text-[#00A3E0]" size={24} />
                  </div>
                  <div className="text-white font-semibold">{industry.name}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[#0089D0] font-semibold tracking-wide uppercase text-sm">Why ATC</span>
              <h2 className="text-4xl md:text-5xl font-bold mt-3 mb-6 text-[#003057]">
                Reliability When It Matters Most
              </h2>
              <p className="text-lg text-[#4A4A4A] mb-8 leading-relaxed">
                Aero Time Critical combines aerospace logistics expertise with responsive service to deliver
                unmatched reliability for your time-sensitive shipments.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <CheckCircle className="text-[#0089D0] flex-shrink-0" size={22} />
                    <span className="text-[#003057] font-medium">{feature}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://customer-assets-m6fa6gv7.emergentagent.net/job_cargo-urgent/artifacts/esv8b4gm_AOG%20ATC%20.png"
                alt="Aero Time Critical branded van delivering an urgent aircraft part beside an aircraft under maintenance"
                className="w-full h-[480px] object-cover rounded-lg shadow-xl"
                loading="lazy"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#C9A227] text-[#002240] p-6 rounded-lg shadow-lg hidden md:block">
                <div className="text-3xl font-bold">150+</div>
                <div className="text-sm font-semibold">Countries Served</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Latest ATC Insights */}
      {latest.length > 0 && (
        <section className="bg-[#F7F9FB] py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-4"
            >
              <div>
                <span className="text-[#0089D0] font-semibold tracking-wide uppercase text-sm">Knowledge Hub</span>
                <h2 className="text-4xl md:text-5xl font-bold mt-3 text-[#003057]">Latest ATC Insights</h2>
              </div>
              <Link to="/insights" className="text-[#0089D0] font-semibold flex items-center gap-2 hover:gap-3 transition-all">
                View All Articles <ArrowRight size={18} />
              </Link>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {latest.map((a, i) => (
                <motion.div
                  key={a.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Link to={`/insights/${a.slug}`} className="block group" data-testid={`home-insight-${i}`}>
                    <div className="bg-white border border-[#E5E9EF] rounded-lg overflow-hidden hover:shadow-xl transition-all h-full">
                      <div className="h-48 overflow-hidden">
                        <img
                          src={mediaUrl(a.featured_image) || 'https://customer-assets-m6fa6gv7.emergentagent.net/job_cargo-urgent/artifacts/dsr13j63_airfreight%20pic%202.png'}
                          alt={a.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-6">
                        <div className="flex items-center gap-2 text-xs font-semibold mb-3">
                          <span className="bg-[#0089D0]/10 text-[#0089D0] px-2 py-1 rounded">{a.category}</span>
                          <span className="text-gray-400">{fmtDate(a.publication_date)}</span>
                        </div>
                        <h3 className="text-xl font-bold text-[#003057] mb-2 group-hover:text-[#0089D0] transition-colors">{a.title}</h3>
                        <p className="text-[#4A4A4A] text-sm line-clamp-3">{a.summary}</p>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="bg-[#003057] py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready for Immediate Response?
            </h2>
            <p className="text-xl text-gray-300 mb-12">
              Our team is standing by 24/7/365 to handle your urgent freight requirements
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="tel:+441753720055">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-primary text-lg"
                  data-testid="cta-call-btn"
                >
                  Call Now: +44 (0) 1753 720055
                </motion.button>
              </a>
              <Link to="/quote">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-secondary text-lg"
                  data-testid="cta-quote-btn"
                >
                  Request a Quote
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
